import { useEffect } from "react";
// ANT DESIGN COMPONENTS
import { notification } from "antd";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import { usePostEmulatorCommandMutation } from "../../redux/monitoring/monitoringAPI";
import { updateSendCommand } from "../../redux/monitoring/monitoringSlice";
// AUTHORIZATION
import GETJwtToken from "../../redux/authentication/authentication";
// INTERFACE
import { RootState } from "../../app/store";
import formatUuidT from "../../flatbuffers/display/uuid";
import notifyCommandBlocked from "../../utils/commandFeedback";

/**
 * Sends operator commands for the selected bank and lift.
 *
 * On EDGE and OFFLINE connections an elevator command is dispatched onto the
 * send-command slice for the monitoring socket to deliver; on EMULATOR it is
 * posted through the emulator mutation. Returns `triggerPostCommand`, which
 * resolves to whether the command was handed to a transport;
 * `isLoadingPostCommand`, true while an emulator request or an edge command is
 * in flight; and the emulator mutation's error and success flags.
 */
export default function usePostCommand() {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const dispatch = useDispatch();

  // ************************************************ */
  // USE STATE VARIABLES **************************** */

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { socketConnection, liftSelected, bankSelected, serverId, sendCommand } = useSelector(
    (state: RootState) => state.monitoring,
  );
  const { theme, userLogged } = useSelector((state: RootState) => state.home);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [
    triggerCommandEmulator,
    {
      isLoading: isLoadingPostCommandEmulator,
      isSuccess: isSuccessPostCommandEmulator,
      isError: isErrorPostCommandEmulator,
      reset: resetPostEmulatorCommand,
    },
  ] = usePostEmulatorCommandMutation();

  // ************************************************ */
  // FUNCTIONS ************************************** */
  /** Report a command that was NOT sent, and say why. See notifyCommandBlocked. */
  const launchBlockedNotification = (message: string, description: string) =>
    notifyCommandBlocked(message, description, theme);

  const getLiftId = () => {
    const selectedId = formatUuidT((liftSelected as any).id);
    if (selectedId) return selectedId;
    const lifts = bankSelected.lifts ?? [];
    let liftId = lifts[0] ? (formatUuidT((lifts[0] as any).id) ?? "") : "";
    if (liftId) {
      lifts.forEach((item: any) => {
        if (item && item.position && item.position === 1) {
          liftId = formatUuidT(item.id) ?? liftId;
        }
      });
      return liftId;
    }
    return "";
  };

  const postCommandEmulator = async (floor: number): Promise<boolean> => {
    const token = await GETJwtToken();
    const BODY = {
      token,
      body: {
        type: "CARCALL",
        lift_id: getLiftId(),
        floor,
        bank_id: formatUuidT((bankSelected as any).id) ?? bankSelected.id,
        front_rear: "front",
      },
      serverId,
    };
    triggerCommandEmulator(BODY);
    return true;
  };

  const postCommandElevator = async (
    floor: number,
    command: number | string,
    active: boolean,
  ): Promise<boolean> => {
    if (!bankSelected || !bankSelected.id) {
      launchBlockedNotification(
        "No bank selected",
        "Select a bank before sending a command. Nothing was sent.",
      );
      return false;
    }
    const resolvedServerId =
      serverId || (bankSelected as any).serverId || (bankSelected as any).idServer || "";
    const BODY = {
      command,
      position: floor,
      active,
      bankId: formatUuidT((bankSelected as any).id) ?? bankSelected.id,
      liftId: formatUuidT((liftSelected as any).id) ?? liftSelected.id,
    };
    dispatch(
      updateSendCommand({
        sendCommand: true,
        serverId: resolvedServerId,
        body: BODY,
      }),
    );
    return true;
  };

  /**
   * Send one command, and report whether it was DELIVERED.
   *
   * The boolean is the caller's only honest signal. `isSuccessPostCommand`
   * below is the EMULATOR mutation's flag and is permanently false on the
   * EDGE/OFFLINE path — which is every production install — so a button that
   * waits on it waits forever. Returning the verdict here is what lets the
   * seven action buttons tell `Blocked` from `Sent` without reading a flag that
   * cannot fire for them.
   *
   * True means "handed to the transport": dispatched onto the send-command
   * slice, or handed to the emulator mutation. It is NOT an acknowledgement —
   * nothing on the wire acknowledges a command. False means one of the four
   * refusal gates fired, each of which has already told the operator why.
   */
  const triggerPostCommand = async ({
    floor,
    command,
    liftType,
    active,
  }: {
    floor: number;
    command: number | string;
    liftType: "ELEVATOR" | "ESCALATOR";
    active: boolean;
  }): Promise<boolean> => {
    if (socketConnection === "EDGE" || socketConnection === "OFFLINE") {
      if (liftType === "ELEVATOR") {
        return postCommandElevator(floor, command, active);
      }
      // Escalators have no elevator-control equivalent on this path. Saying so
      // is the point: the old code fell out of the `if` and returned silently,
      // which is indistinguishable from a command that was sent and ignored.
      launchBlockedNotification(
        "Command not supported",
        `${liftType} devices cannot be commanded from this panel. Nothing was sent.`,
      );
      return false;
    }
    if (socketConnection === "EMULATOR") {
      return postCommandEmulator(floor);
    }
    // No connection mode. `socketConnection` initialises to
    // REACT_APP_EDGE_SERVER_CONNECTION || "" (monitoringSlice), and the only
    // control that can set EDGE/OFFLINE at runtime is itself gated on that same
    // variable — so a build without it leaves EVERY action button inert. This
    // used to be an unreachable `else` with no body, which is exactly why the
    // failure reached sites as "actions never get to the vendor".
    launchBlockedNotification(
      "No connection mode configured",
      socketConnection
        ? `Unknown connection mode "${socketConnection}". Expected EDGE, OFFLINE or EMULATOR. Nothing was sent.`
        : "REACT_APP_EDGE_SERVER_CONNECTION is not set for this build, so commands cannot be routed. Nothing was sent.",
    );
    return false;
  };

  const launchSuccessNotification = () => {
    if (userLogged.commandNotifications) {
      notification.success({
        message: "Command sent successfully",
        description: `${socketConnection} Command`,
        placement: "top",
        className: `monitoring__notification__${theme}`,
      });
    }
  };

  const launchErrorNotification = () => {
    if (userLogged.commandNotifications) {
      notification.error({
        message: "Error",
        description: `${socketConnection} Command`,
        placement: "top",
        className: `monitoring__notification__${theme}`,
      });
    }
  };

  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    if (isSuccessPostCommandEmulator) {
      launchSuccessNotification();
      resetPostEmulatorCommand();
    }
  }, [isSuccessPostCommandEmulator]);

  useEffect(() => {
    if (isErrorPostCommandEmulator) {
      launchErrorNotification();
      resetPostEmulatorCommand();
    }
  }, [isErrorPostCommandEmulator]);

  // ************************************************ */
  // COMPONENT ************************************** */

  // An elevator command is in flight from the moment it is dispatched until a
  // MonitoringWebSocket claims it (or the watchdog gives up) and clears the
  // flag. Without this the six action buttons reported the EMULATOR mutation's
  // loading state on the edge path, where it is permanently false — so the
  // spinner never appeared and every button looked idle mid-command.
  const isElevatorCommandInFlight = Boolean(sendCommand?.sendCommand);

  // WARNING — `isSuccessPostCommand` / `isErrorPostCommand` are the EMULATOR
  // mutation's flags and NOTHING ELSE. The EDGE/OFFLINE path never touches that
  // mutation, so on every production install both are permanently false and any
  // `useEffect(..., [isSuccessPostCommand])` in a button never runs. They are
  // kept only for the emulator screens that genuinely post through RTK Query.
  // For "did my command go out?", await `triggerPostCommand` instead.
  return {
    triggerPostCommand,
    isLoadingPostCommand: isLoadingPostCommandEmulator || isElevatorCommandInFlight,
    isErrorPostCommand: isErrorPostCommandEmulator,
    isSuccessPostCommand: isSuccessPostCommandEmulator,
  };
}
